import { type IBooleanCondition } from '../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';
import { type IAdvancedRichText } from '../../inputs/rich-text-advanced/IAdvancedRichText.ts';
import { type IButtonWidget } from './repository/advanced/buttons/IButtonWidget.ts';
import { type ICompanyWidget } from './repository/advanced/company/ICompanyWidget.tsx';
import { type IConfirmWidget } from './repository/advanced/confirm/IConfirmWidget.ts';
import { type IRepeatControllerWidget } from './repository/advanced/repeat-controller/IRepeatControllerWidget.ts';
import { type IRepeatableWidget } from './repository/advanced/repeatable/IRepeatableWidget.ts';
import { type ISubsectionWidget } from './repository/advanced/subsection/ISubsectionWidget.ts';
import { type ITableInputWidget } from './repository/advanced/table-input/ITableInputWidget.ts';
import {
  type IComputedBooleanWidget,
  type IComputedNumberWidget,
  type IComputedStringWidget,
} from './repository/computed/IComputedWidget.ts';
import { type IColumnWidget } from './repository/containers/column/IColumnWidget.ts';
import { type IQuestionWidget } from './repository/containers/question/IQuestionWidget.ts';
import { type IRootWidget } from './repository/containers/root/IRootWidget.ts';
import { type IRowWidget } from './repository/containers/row/IRowWidget.ts';
import { type IDividerWidget } from './repository/graphics/divider/IDividerWidget.ts';
import { type IInfoWidget } from './repository/graphics/info/IInfoWidget.ts';
import { type IPageWidget } from './repository/graphics/page/IPageWidget.ts';
import { type IParagraphWidget } from './repository/graphics/paragraph/IParagraphWidget.ts';
import { type IWidgetAdvancedOptionValue } from './repository/inputs/advanced-select-common/form/createAdvancedSelectValueSchema.ts';
import { type IAsyncSelectWidget } from './repository/inputs/async-select/IAsyncSelectWidget.ts';
import { type IBooleanWidget } from './repository/inputs/boolean/IBooleanWidget.ts';
import { type ICurrencyWidget } from './repository/inputs/currency/ICurrencyWidget.ts';
import { type IDateWidget } from './repository/inputs/date/IDateWidget.ts';
import { type IHiddenWidget } from './repository/inputs/hidden/IHiddenWidget.ts';
import { type IMultiSelectWidget } from './repository/inputs/multi-select/IMultiSelectWidget.ts';
import { type INumberWidget } from './repository/inputs/number/INumberWidget.ts';
import { type ISingleSelectWidget } from './repository/inputs/single-select/ISingleSelectWidget.ts';
import { type ITextWidget } from './repository/inputs/text/ITextWidget.ts';

/**
 * Definisce il tipo di elemento strutturale (di input, container, grafico, root...)
 */
export enum WidgetMainType {
  Container = 'container',
  Input = 'input',
  Root = 'root',
  Graphic = 'graphic',
  Base = 'base',
}

/**
 * Tipologia di widget utilizzata per la renderizzazione del
 * menù a tendina.
 */
export enum WidgetCategory {
  Container = 'container',
  Input = 'input',
  Advanced = 'advanced',
  Root = 'root',
  Graphic = 'graphic',
  Computed = 'computed',
}

/**
 * Tipi di elemento, utili per discriminare univocamente l'elemento richiesto.
 */
export enum WidgetType {
  // Root
  Root = 'root',
  // Container
  Row = 'row',
  Column = 'column',
  Question = 'question',
  Repeatable = 'repeatable',
  Subsection = 'subsection',
  // Input
  TableInput = 'tableInput',
  Text = 'text',
  Number = 'number',
  Currency = 'currency',
  Date = 'date',
  Computed = 'computed',
  ComputedBoolean = 'computedBoolean',
  ComputedString = 'computedString',
  ComputedNumber = 'computedNumber',
  ComputedDate = 'computedDate',
  Hidden = 'hidden',
  Select = 'select',
  AdvancedSelect = 'advancedSelect',
  AdvancedMultiSelect = 'advancedMultiSelect',
  MultiSelect = 'multiSelect',
  AsyncSelect = 'asyncSelect',
  AsyncValue = 'asyncValue',
  File = 'file',
  Boolean = 'boolean', // checkbox
  Radio = 'radio',
  RadioGroup = 'radioGroup',
  // Graphic
  Info = 'info',
  Divider = 'divider',
  PageBreak = 'pageBreak',
  Paragraph = 'paragraph',
  Page = 'page',
  // Advanced
  Button = 'button',
  Confirm = 'confirm',
  RepeatController = 'repeatController',
  Switch = 'switch',
  PaymentRow = 'paymentRow',
  Company = 'company',
}

/**
 * Union type dei tre tipi base di widget
 */
export type Widget =
  | IBooleanWidget
  | ICurrencyWidget
  | IDateWidget
  | INumberWidget
  | IMultiSelectWidget
  | ISingleSelectWidget
  | IAsyncSelectWidget
  | ITextWidget
  | IDividerWidget
  | IInfoWidget
  | IPageWidget
  | IParagraphWidget
  | IColumnWidget
  | IQuestionWidget
  | IRowWidget
  | IComputedBooleanWidget
  | IComputedStringWidget
  | IComputedNumberWidget
  | IButtonWidget
  | IConfirmWidget
  | IRepeatableWidget
  | ISubsectionWidget
  | ITableInputWidget
  | IRepeatControllerWidget
  | IHiddenWidget
  | ICompanyWidget;

export type WidgetOrRoot = Widget | IRootWidget;

/**
 * Dati contenuti in nel sortable, restutiti dalle funzioni di drag&drop,
 * utili per le operazioni relative allo spostamento dei widget.
 */
export type ISortableWidgetData = Pick<Widget, 'order' | 'path'> & {
  type: WidgetType;
  /** Rappresenta le aree di drop extra (e.g. dei componenti container) */
  directionalDroppableType?: DirectionalDroppableType | null;
  /** Utilizzato dalle aree di drop extra per indicare l'id originale del widget */
  originalId?: string;
  /** Indica se il widget selezionato è un widget "reale" (in configurazione) o un template (dalla barra laterale) */
  template?: boolean;
  /** Indica se il droppable è quello di default "iniziale", sempre presente per permettere il DnD in una configurazione vuota*/
  isDefaultDroppable?: boolean;
};

export enum DirectionalDroppableType {
  Before = 'before',
  After = 'after',
}

export type IWidgetOption = IWidgetOptionValue | IWidgetOptionDivider;

export interface IWidgetAdvancedOption {
  type: 'option';
  visible?: IBooleanCondition;
  enabled?: IBooleanCondition;
  /** Valori multipli per widget con colonne (chiave: column.id, valore: string) */
  values?: IWidgetAdvancedOptionValue;
}

export interface IWidgetOptionValue {
  type: 'option';
  visible?: IBooleanCondition;
  enabled?: IBooleanCondition;
  value: any;
  label: IAdvancedRichText;
  /** Valori multipli per widget con colonne (chiave: column.id, valore: string) */
  values?: Record<string, string>;
}

export function isOptionValue(
  option: IWidgetOption,
): option is IWidgetOptionValue {
  return option.type === 'option';
}

export interface IWidgetOptionDivider {
  type: 'divider';
  visible?: IBooleanCondition;
  label: IAdvancedRichText;
}

/**
 * Disposizione dell'etichetta del widget.
 * `Block` = etichetta nel flusso sopra il campo, a piena larghezza e con
 * a capo automatico; `Floating` = resa flottante attuale.
 */
export enum LabelLayout {
  Floating = 'floating',
  Block = 'block',
}

export interface IBaseLayoutOptions {
  /** Utilizzato per gestire lo spazio occupato dal widget (in colonne) posizionato in un container Row */
  colSpan?: number;
  /** Utilizzato per gestire la larghezza delle colonne in una tabella */
  tableColumnWidth?: number;
  /**
   * Utilizzato per indicare se il layout é interno ad un tabella
   * Attualmente impostato a true di default, quando viene creato all'interno di una tabella
   * Se in futuro diventa possibile spostare un widget tra una tabella ed il layout andrebbe gestito
   */
  isTableColumn?: boolean;
  /**
   * Opzionale e senza default: assente = comportamento attuale (compatto).
   * Oggi ha effetto solo sui widget di scelta nelle varianti radio/checkbox;
   * sugli altri widget viene ignorata (ma conservata, es. passando a tendina).
   */
  labelLayout?: LabelLayout | null;
}

export type IWidgetPath = Array<string | number>;
